import { paramCleaner } from '@/helpers/formatters';
import {
  postRequest, putRequest, getRequest, getBaseUrl,
} from '@/api/request';
import { isEmpty } from 'lodash-es';

export const downloadEmployeeAcknowledgementsCsv = async ({
  params = {}, selectedFilters, searchTerm,
}) => {
  const {
    sort, page, q, ...paramFilters
  } = params;

  const cleanedParams = paramCleaner(paramFilters);

  const searchParams = new URLSearchParams();

  if (typeof sort !== 'undefined') searchParams.append('sort', sort);
  else if (searchTerm) searchParams.append('sort', 'relevance');

  return postRequest({
    url: `/jobprofile/employee-acknowledgement/download-csv?${searchParams}`,
    body: {
      filters: !isEmpty(selectedFilters) ? selectedFilters : cleanedParams,
      textFilter: searchTerm,
    },
    isCsv: true,
    timeout: 60000,
  });
};

export const getEmployeeAcknowledgementPreview = (employeeUlid) => getRequest({
  url: `/jobprofile/employee-acknowledgement/sidebar?employee_ulid=${encodeURIComponent(employeeUlid)}`,
});

export const getEmployeeAcknowledgementViewPdfUrl = (acknowledgementUlid) => `${getBaseUrl()}/jobprofile/employee-acknowledgement/view-pdf?acknowledgement_ulid=${encodeURIComponent(acknowledgementUlid)}`;

export const getEmployeeAcknowledgementDownloadPdfUrl = (acknowledgementUlid) => `${getBaseUrl()}/jobprofile/employee-acknowledgement/download-pdf?acknowledgement_ulid=${encodeURIComponent(acknowledgementUlid)}`;

export const getEmployeeAcknowledgementDownloadZipUrl = (acknowledgementUlids) => {
  const params = acknowledgementUlids
    .map((ulid) => `acknowledgement_ulids[]=${encodeURIComponent(ulid)}`)
    .join('&');

  return `${getBaseUrl()}/jobprofile/employee-acknowledgement/download-zip?${params}`;
};

export const getEmployeeAcknowledgementSummary = async ({
  selectedFilters, searchTerm, params = {},
} = {}) => {
  const {
    sort, page, q, ...paramFilters
  } = params;

  const cleanedParams = paramCleaner(paramFilters);

  return postRequest({
    url: '/jobprofile/employee-acknowledgement/summary',
    body: {
      filters: !isEmpty(selectedFilters) ? selectedFilters : cleanedParams,
      textFilter: searchTerm,
    },
  });
};

export const getEmployeeAcknowledgements = async ({
  params = {}, selectedFilters, searchTerm,
}) => {
  const {
    sort, page, q, ...paramFilters
  } = params;

  const cleanedParams = paramCleaner(paramFilters);

  const searchParams = new URLSearchParams();
  if (typeof sort !== 'undefined') searchParams.append('sort', sort);
  else if (searchTerm) searchParams.append('sort', 'relevance');

  if (typeof page !== 'undefined') searchParams.append('page', page);

  const {
    items = [],
    filters = [],
    sorting = {},
    pagination = {},
    owned = {},
    textFilter = {},
  } = await postRequest({
    url: `/jobprofile/employee-acknowledgement/documents?${searchParams}`,
    body: {
      filters: !isEmpty(selectedFilters) ? selectedFilters : cleanedParams,
      textFilter: searchTerm,
    },
  });
  return {
    items,
    filters,
    sorting,
    pagination,
    owned,
    textFilter,
  };
};

export const createEmployeeAcknowledgement = ({ employeeUlids }) => postRequest({
  url: '/jobprofile/employee-acknowledgement/create',
  body: { employee_ulids: employeeUlids },
});

export const cancelEmployeeAcknowledgement = ({ employeeUlids }) => putRequest({
  url: '/jobprofile/employee-acknowledgement/cancel',
  body: { employee_ulids: employeeUlids },
});

export const sendEmployeeAcknowledgementReminder = ({ employeeUlids }) => postRequest({
  url: '/jobprofile/employee-acknowledgement/reminder',
  body: { employee_ulids: employeeUlids },
});

export const getJobProfileDrafts = async ({
  mode = 'draft', params = {}, selectedFilters, selectedMyJobs, searchTerm,
} = {}) => {
  const {
    sort, page, q, size, ...paramFilters
  } = params;

  const cleanedParams = paramCleaner(paramFilters);

  const searchParams = new URLSearchParams({ mode });
  if (typeof sort !== 'undefined') searchParams.append('sort', sort);
  else if (searchTerm) searchParams.append('sort', 'relevance');

  if (typeof page !== 'undefined') searchParams.append('page', page);
  if (typeof size !== 'undefined') searchParams.append('size', size);

  const {
    documents = [],
    filters = [],
    sorting = {},
    pagination = {},
    totals = {},
    owned = {},
    textFilter = {},
  } = await postRequest({
    url: `/jobprofile/drafting/documents?${searchParams}`,
    body: {
      filters: !isEmpty(selectedFilters) ? selectedFilters : cleanedParams,
      owned: selectedMyJobs,
      textFilter: searchTerm,
    },
  });
  return {
    documents,
    filters,
    sorting,
    pagination,
    totals,
    owned,
    textFilter,
  };
};

export const getDraftProfilesByUlids = async (ulids) => {
  const listOfUlids = Array.isArray(ulids) ? ulids : [];
  const params = new URLSearchParams();

  listOfUlids.forEach((ulid) => params.append('ulids[]', ulid));

  return getRequest({ url: `/jobprofile/drafting/profiles?${params.toString()}` });
};

export const getJobProfiles = async ({
  params = {}, selectedFilters, selectedMyJobs, searchTerm,
} = {}) => {
  const {
    sort, page, q, ...paramFilters
  } = params;

  const cleanedParams = paramCleaner(paramFilters);

  const searchParams = new URLSearchParams({});
  if (typeof sort !== 'undefined') searchParams.append('sort', sort);
  else if (searchTerm) searchParams.append('sort', 'relevance');

  if (typeof page !== 'undefined') searchParams.append('page', page);

  const {
    items = [],
    sorting = {},
    pagination = {},
    textFilter = {},
  } = await postRequest({
    url: `/jobprofile/library/documents?${searchParams}`,
    body: {
      filters: !isEmpty(selectedFilters) ? selectedFilters : cleanedParams,
      owned: selectedMyJobs,
      textFilter: searchTerm,
    },
  });
  return {
    documents: items,
    sorting,
    pagination,
    textFilter,
  };
};

export const getJobProfileTemplates = async ({
  params = {}, searchTerm,
} = {}) => {
  const { sort, page } = params;

  const searchParams = new URLSearchParams();
  if (typeof sort !== 'undefined') searchParams.append('sort', sort);
  if (typeof page !== 'undefined') searchParams.append('page', page);

  const {
    items = [],
    pagination = {},
    sorting = {},
    textFilter = {},
  } = await postRequest({
    url: `/jobprofile/library/templates?${searchParams}`,
    body: { textFilter: searchTerm },
  });

  return {
    documents: items,
    pagination,
    sorting,
    textFilter,
  };
};

export const getAcknowledgement = async ({ ulid, mode }) => {
  const searchParams = new URLSearchParams({ mode });

  // TODO: use actual API to get single acknowledgement when available
  const { items = [] } = await postRequest({
    url: `/jobprofile/acknowledgement/documents?${searchParams}`,
    body: { filters: {} },
  });

  return items.find((att) => att.ulid === ulid);
};

export const createJobProfileTemplate = async ({ title = '' }) => postRequest({
  url: '/jobprofile/drafting/create-template',
  body: { title },
});

export const createJobProfile = async ({
  jobCode, title, location, templateUlid, profileUlid,
}) => postRequest({
  url: '/jobprofile/drafting/create-profile',
  body: {
    job_code: jobCode,
    title,
    location,
    template_ulid: templateUlid,
    profile_ulid: profileUlid,
  },
});

export const getJobProfile = ({ ulid, rescore = '', finalized = '' }) => getRequest({
  url: `/jobprofile/editor/init?ulid=${ulid}&rescore=${rescore}&finalized=${finalized}`,
});
export const getJobProfileHris = ({ ulid, rescore }) => getRequest({
  url: `/jobprofile/editor/init-hris?ulid=${ulid}&rescore=${rescore}`,
});

export const getJobProfileRevisions = ({ ulid }) => getRequest({ url: `/jobprofile/editor/revisions?ulid=${ulid}` });
export const getJobProfileVersions = ({ ulid }) => getRequest({ url: `/jobprofile/editor/versions?ulid=${ulid}` });

export const getJobProfileRevision = ({ ulid, revisionUlid }) => getRequest({
  url: `/jobprofile/editor/revision?ulid=${ulid}&revision_ulid=${revisionUlid}`,
});

export const finalizeJobProfileTemplate = ({ ulid }) => postRequest({
  url: '/jobprofile/drafting/finalize-template',
  body: { ulid },
});

export const getFinalizedProfileTemplates = () => getRequest({
  url: '/jobprofile/drafting/get-finalized-templates',
});

export const getProfilesByTemplate = ({ ulid }) => getRequest({
  url: `/jobprofile/drafting/get-profiles-by-template?ulid=${ulid}`,
});

export const applyJobProfileTemplate = ({ ulid, templateUlid }) => postRequest({
  url: '/jobprofile/drafting/apply-template',
  body: { ulid, template_ulid: templateUlid },
});

export const rescoreJobProfile = ({ ulid, payload }) => postRequest({
  url: '/jobprofile/editor/rescore',
  body: {
    ulid,
    ...payload,
  },
});

export const deleteJobProfile = ({ ulid }) => postRequest({
  url: '/jobprofile/editor/delete',
  body: { ulid },
});

export const restoreJobProfileRevision = ({ ulid, revisionUlid, revisionName }) => postRequest({
  url: '/jobprofile/editor/restore-revision',
  body: { ulid, revision_ulid: revisionUlid, revision_name: revisionName },
});

export const renameJobProfileRevision = ({ ulid, revisionUlid, name }) => postRequest({
  url: '/jobprofile/drafting/rename-revision',
  body: { ulid, revision_ulid: revisionUlid, name },
});

export const searchJobProfilesForWizard = ({ title = '', jobCode = '' } = {}) => postRequest({
  url: '/jobprofile/library/search-for-create',
  body: { title, jobCode },
});

export const duplicateJobProfile = ({ ulid }) => postRequest({
  url: '/jobprofile/drafting/duplicate-document',
  body: { ulid },
});

export const linkHrisProfile = ({ draftUlid, hrisUlid }) => postRequest({
  url: '/jobprofile/editor/link-hris-profile',
  body: { ulid: draftUlid, hris_ulid: hrisUlid },
});

export const updateProfileMemo = ({ ulid, memo }) => postRequest({
  url: '/jobprofile/editor/update-memo',
  body: { ulid, memo },
});

export const getFlsaForms = () => getRequest({
  url: '/jobprofile/flsa/forms',
});

export const createFlsaSubmission = ({
  jobProfileUlid,
  jobProfileVersionUlid,
  draftJobProfileUlid,
  draftRevisionUlid,
  flsaFormUlid,
  dataJson,
}) => {
  const body = {
    flsa_form_ulid: flsaFormUlid,
    data_json: dataJson,
  };

  if (jobProfileUlid) {
    body.job_profile_ulid = jobProfileUlid;
  }

  if (jobProfileVersionUlid) {
    body.job_profile_version_ulid = jobProfileVersionUlid;
  }

  if (draftJobProfileUlid) {
    body.draft_job_profile_ulid = draftJobProfileUlid;
  }

  if (draftRevisionUlid) {
    body.draft_job_profile_revision_ulid = draftRevisionUlid;
  }

  return postRequest({
    url: '/jobprofile/flsa/create-submission',
    body,
  });
};

export const getFlsaSubmission = ({ ulid }) => getRequest({
  url: `/jobprofile/flsa/get-submission?ulid=${ulid}`,
});

export const getFlsaSubmissions = ({
  flsaFormUlid,
  jobProfileUlid,
  draftJobProfileUlid,
}) => {
  const params = new URLSearchParams();

  if (flsaFormUlid) {
    params.set('flsa_form_ulid', flsaFormUlid);
  }

  if (jobProfileUlid) {
    params.set('job_profile_ulid', jobProfileUlid);
  }

  if (draftJobProfileUlid) {
    params.set('draft_job_profile_ulid', draftJobProfileUlid);
  }

  return getRequest({
    url: `/jobprofile/flsa/get-submissions?${params}`,
  });
};

export const searchDraftProfileUsers = ({ q, draftUlid }) => getRequest({
  url: '/jobprofile/editor/search-users'
    + `?q=${encodeURIComponent(q)}&ulid=${encodeURIComponent(draftUlid)}`,
});

export const grantDraftProfileAccess = ({ draftUlid, userUlid }) => postRequest({
  url: '/jobprofile/editor/grant-access'
    + `?ulid=${encodeURIComponent(draftUlid)}&user_ulid=${encodeURIComponent(userUlid)}`,
  body: {},
});

export const revokeDraftProfileAccess = ({ draftUlid, userUlid }) => postRequest({
  url: '/jobprofile/editor/revoke-access'
    + `?ulid=${encodeURIComponent(draftUlid)}&user_ulid=${encodeURIComponent(userUlid)}`,
  body: {},
});
